import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {forkJoin, of} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {ObjectType} from '../../../shared/models/object.model';
import {Tag, TagType} from '../../../shared/models/tag';
import {FilterLeaf, FilterNode, FilterRequest, Filters, FiltersConfig} from '../../../shared/models/filter';
import {Device, DeviceId} from '../../../shared/models/device';
import {Option, OptionGroupMultiSelect} from '../../../shared/models/option';
import {TagService} from '../../../core/services/tag.service';
import {ClickService} from '../../../core/services/click.service';
import {UrlService} from '../../../core/services/url.service';
import {DevicesService} from '../../../core/services/devices.service';
import {TagInputComponent} from '../tag-input/tag-input.component';
import {PopoverMultiSelectComponent} from '../popover-multi-select/popover-multi-select.component';
import {SpinnerComponent} from '../spinner/spinner.component';
import {PillComponent} from '../pill/pill.component';
import {formatTagLabel} from '../../../shared/utils/string-utils';
import {sortByProfileOrder} from '../../../shared/constants/tag-profiles.constants';
import {facetSelectionsFromFilters, facetSelectionsToFilterNodes} from '../../../shared/utils/facet.utils';

@Component({
  selector: 'motn-tile-tag-filter-panel',
  templateUrl: './tile-tag-filter-panel.component.html',
  styleUrls: ['./tile-tag-filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TagInputComponent,
    PopoverMultiSelectComponent,
    SpinnerComponent,
    CdkOverlayOrigin,
    PillComponent,
  ],
})
export class TileTagFilterPanelComponent implements OnInit {
  @Input() filtersConfig: FiltersConfig;
  @Input() searchInputRef: ElementRef;
  @Output() clickedOutside = new EventEmitter<void>();

  readonly ObjectType = ObjectType;
  readonly formatTagLabel = formatTagLabel;
  readonly facetOverlayPositions: ConnectedPosition[] = [
    {originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4, offsetX: 4},
    {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4, offsetX: 4},
  ];

  private readonly destroyRef = inject(DestroyRef);

  loading = true;

  // Devices
  devices: Device[] = [];
  loadingDevices = false;
  selectedDeviceIds: DeviceId[] = [];
  deviceOptionGroup: OptionGroupMultiSelect = new OptionGroupMultiSelect();
  openKey: string | null = null;

  // Facets — FACET-type tags loaded from TagService
  facetTags: Tag[] = [];
  facetValues: Record<string, string> = {};

  // Tags — TAG-type tags used as filters
  tagFilterApplied: Tag[] = [];

  constructor(
    private eRef: ElementRef,
    private clickService: ClickService,
    private urlService: UrlService,
    private tagService: TagService,
    private devicesService: DevicesService,
  ) {}

  ngOnInit(): void {
    let initialFilters: Filters | null = null;
    this.urlService.filters$.pipe(take(1)).subscribe(filters => {
      initialFilters = filters;
      this.initFromFilters(filters);
    });

    const deviceLoad$ = this.selectedDeviceIds.length
      ? this.devicesService.fetchUserDevices$().pipe(
          take(1),
          catchError(() => of([] as Device[])),
        )
      : of([] as Device[]);

    forkJoin({
      tags: this.tagService.getUserTags$().pipe(take(1)),
      devices: deviceLoad$,
    }).subscribe(({tags, devices}) => {
      this.facetTags = sortByProfileOrder(tags.filter(t => t.Type === TagType.FACET));

      if (devices.length) {
        this.devices = devices;
        this.buildDeviceOptionGroup();
      }

      if (initialFilters?.tagFilters?.Filters) {
        const tagsById = new Map(
          tags.filter(t => t.Type === TagType.TAG).map(t => [t.ID, t])
        );
        this.tagFilterApplied = initialFilters.tagFilters.Filters
          .filter((node): node is FilterLeaf =>
            'Key' in node && node.Key === 'tag' && Boolean(node.ID) && !node.Value
          )
          .map(node => tagsById.get(node.ID!))
          .filter((tag): tag is Tag => Boolean(tag));
      }

      this.loading = false;
    });

    this.clickService.click$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe($el => {
      const inPanel = this.eRef.nativeElement.contains($el);
      const inSearch = this.searchInputRef?.nativeElement.contains($el);
      if (!inPanel && !inSearch) {
        this.openKey = null;
        this.clickedOutside.emit();
      }
    });
  }

  get deviceTriggerLabel(): string {
    if (!this.selectedDeviceIds.length) return '';
    if (this.selectedDeviceIds.length === 1) return this.getDeviceName(this.selectedDeviceIds[0]);
    return `${this.selectedDeviceIds.length} devices`;
  }

  getDeviceName(id: DeviceId): string {
    return this.devices.find(d => d.ID === id)?.Name
      ?? this.devicesService.sessUserDevices[id]?.Name
      ?? id;
  }

  private buildDeviceOptionGroup(): void {
    const group = new OptionGroupMultiSelect();
    group.options = this.devices.map(d => ({
      id: d.ID,
      name: d.Name,
      selected: this.selectedDeviceIds.includes(d.ID),
    }));
    this.deviceOptionGroup = group;
  }

  getFacetOptionGroup(facet: Tag): OptionGroupMultiSelect {
    const group = new OptionGroupMultiSelect();
    group.options = this.tagService.getFacetValues(facet.ID).map(v => ({
      id: v,
      name: v,
      selected: this.facetValues[facet.ID] === v,
    }));
    return group;
  }

  toggleDeviceOpen(): void {
    if (this.openKey === 'devices') {
      this.openKey = null;
      return;
    }
    this.openKey = 'devices';
    this.loadDevices();
  }

  private loadDevices(): void {
    if (this.devices.length || this.loadingDevices) return;
    this.loadingDevices = true;
    this.devicesService.fetchUserDevices$().pipe(take(1)).subscribe({
      next: devices => {
        this.devices = devices;
        this.loadingDevices = false;
        this.buildDeviceOptionGroup();
      },
      error: () => {
        this.loadingDevices = false;
      },
    });
  }

  onDeviceToggled(option: Option): void {
    const id = option.id as DeviceId;
    if (this.selectedDeviceIds.includes(id)) {
      this.selectedDeviceIds = this.selectedDeviceIds.filter(d => d !== id);
    } else {
      this.selectedDeviceIds = [...this.selectedDeviceIds, id];
    }
    this.saveFilters();
  }

  removeDevice(deviceId: DeviceId): void {
    this.selectedDeviceIds = this.selectedDeviceIds.filter(id => id !== deviceId);
    this.buildDeviceOptionGroup();
    this.saveFilters();
  }

  clearDevices(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedDeviceIds = [];
    this.buildDeviceOptionGroup();
    this.saveFilters();
  }

  toggleFacetOpen(key: string): void {
    this.openKey = this.openKey === key ? null : key;
  }

  onFacetSelected(key: string, option: Option): void {
    if (this.facetValues[key] === option.id) {
      const copy = {...this.facetValues};
      delete copy[key];
      this.facetValues = copy;
    } else {
      this.facetValues = {...this.facetValues, [key]: option.id};
    }
    this.openKey = null;
    this.saveFilters();
  }

  clearFacet(key: string, event: MouseEvent): void {
    event.stopPropagation();
    const copy = {...this.facetValues};
    delete copy[key];
    this.facetValues = copy;
    this.saveFilters();
  }

  onTagsChanged(tags: Tag[]): void {
    this.tagFilterApplied = tags;
    this.saveFilters();
  }

  private initFromFilters(filters: Filters | null): void {
    if (!filters) return;
    this.selectedDeviceIds = filters.devices ?? [];
    this.facetValues = facetSelectionsFromFilters(filters);
  }

  private saveFilters(): void {
    const filterNodes: FilterNode[] = [
      ...facetSelectionsToFilterNodes(this.facetValues, this.facetTags),
      ...this.tagFilterApplied.map(t => ({Key: 'tag', ID: t.ID, Label: t.Label} as FilterNode)),
    ];

    const tagFilters: FilterRequest | undefined = filterNodes.length
      ? {Operator: 'and', Filters: filterNodes}
      : undefined;

    const filters: Filters = {devices: [...this.selectedDeviceIds]};
    if (tagFilters) {
      filters.tagFilters = tagFilters;
    }

    this.urlService.setFilters(filters);
  }
}
